import { useFormContext } from 'react-hook-form';
import { Repeat, Send, ShieldCheck, type LucideIcon } from 'lucide-react';
import SectionCard from '../SectionCard';
import { useRoleEditorContext } from '../RoleEditorContext';
import { SOVEREIGN } from '../../../../epc/utils/epcProjectVisual';
import type { RoleEditorFormValues } from '../types';

type AccessChoice = 'approver' | 'requester' | 'both';

interface ChoiceDef {
  value: AccessChoice;
  label: string;
  help: string;
  icon: LucideIcon;
  recommended?: boolean;
}

const CHOICES: ChoiceDef[] = [
  {
    value: 'approver',
    label: 'Approver',
    help: "Reviews and approves submissions from other roles. Doesn't submit their own.",
    icon: ShieldCheck,
  },
  {
    value: 'requester',
    label: 'Requester',
    help: "Submits work items that need approval. Doesn't approve.",
    icon: Send,
  },
  {
    value: 'both',
    label: 'Approver + Requester',
    help: 'Can do both — common for managers.',
    icon: Repeat,
    recommended: true,
  },
];

/**
 * Step 3 of 6 — Access type.
 *
 * Hidden entirely for mobile-app users (they neither approve nor submit
 * through the approval chain). The sidebar marks this section as `'na'`
 * in that case; returning `null` here keeps the main canvas in sync.
 */
export default function AccessTypeSection() {
  const { setValue } = useFormContext<RoleEditorFormValues>();
  const { isMobileUser, isReadonly, selectedRoleType, setSelectedRoleType } =
    useRoleEditorContext();

  if (isMobileUser) return null;

  const handleSelect = (value: AccessChoice) => {
    if (isReadonly) return;
    setSelectedRoleType(value);
    setValue('roleType', value, { shouldDirty: true });
  };

  return (
    <SectionCard
      icon={ShieldCheck}
      title="Access type"
      sub="How users in this role interact with approvals."
      step={3}
      totalSteps={6}
    >
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-3"
        role="radiogroup"
        aria-label="Access type"
      >
        {CHOICES.map((choice) => {
          const isActive = selectedRoleType === choice.value;
          const Icon = choice.icon;
          return (
            <button
              key={choice.value}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={isReadonly}
              onClick={() => handleSelect(choice.value)}
              className="relative text-left rounded-xl p-4 cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              style={
                isActive
                  ? {
                      border: `2px solid ${SOVEREIGN.brand}`,
                      background: 'rgba(51,146,116,0.07)',
                    }
                  : {
                      border: `2px solid ${SOVEREIGN.cardBorder}`,
                      background: SOVEREIGN.cardBg,
                    }
              }
            >
              {choice.recommended && (
                <span
                  className="absolute top-2 right-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider text-white"
                  style={{ backgroundColor: SOVEREIGN.bottle }}
                >
                  Default
                </span>
              )}
              <Icon
                size={18}
                className="mb-2"
                style={{
                  color: isActive ? SOVEREIGN.brand : SOVEREIGN.barTrack,
                }}
              />
              <div
                className="text-sm font-semibold leading-tight"
                style={{
                  color: isActive ? SOVEREIGN.bottle : SOVEREIGN.ink,
                }}
              >
                {choice.label}
              </div>
              <p
                className="mt-1 text-[11px] leading-snug"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                {choice.help}
              </p>
            </button>
          );
        })}
      </div>
    </SectionCard>
  );
}
